/**
 * Frontend bridge: link intercept, PJAX, Mid Point handoff, glimm sweep.
 */

import { createShader, playSweep } from 'glimm';
import {
  aestheticsToSweepOptions,
  animateRegionOpacity,
  areContentRegionsCompatible,
  clearPendingHandoff,
  clearRegionOpacity,
  closeMobileNavigation,
  debugLog,
  debugPhase,
  debugWarn,
  findContentRegion,
  getContentFadeTiming,
  isSamePage,
  matchesLinkScope,
  normalizeHref,
  prefersReducedMotion,
  readPendingHandoff,
  removeMidpointLandingStyle,
  restorePageStyles,
  runPostNavigationUpdates,
  savePendingHandoff,
  setRegionOpacity,
  shaderOptionsFromAesthetics,
  shouldSkipLink,
  snapshotPageStyles,
  swapContentRegions,
  syncPageStyles,
} from './utils.js';

const config = window.glimmTransitions || {};
let shaderCtrl = null;
let canvasEl = null;
let navigating = false;
let activeHandle = null;

function log(...args) {
  debugLog(config, ...args);
}

function phase(name, data) {
  debugPhase(config, name, data);
}

function warn(...args) {
  debugWarn(config, ...args);
}

function resetShaderForSweep(ctrl) {
  ctrl.setProgress(0);
  ctrl.setAlpha(1);
  phase('shader:reset', {
    progress: ctrl.getProgress(),
    alpha: ctrl.getAlpha(),
  });
}

function cancelActiveTransition(reason) {
  if (!navigating && !activeHandle) return;

  phase('transition:cancel', { reason });

  if (activeHandle?.cancel) {
    activeHandle.cancel();
    activeHandle = null;
  }

  clearRegionOpacity(findContentRegion(document));
  navigating = false;
}

function getAesthetics() {
  return config.aesthetics || {};
}

function getSweepOptions(overrides = {}) {
  return {
    ...aestheticsToSweepOptions(getAesthetics()),
    ...overrides,
  };
}

function ensureCanvas() {
  if (canvasEl) return canvasEl;

  canvasEl = document.createElement('canvas');
  canvasEl.id = 'glimm-transitions-canvas';
  canvasEl.setAttribute('aria-hidden', 'true');
  Object.assign(canvasEl.style, {
    position: 'fixed',
    inset: '0',
    width: '100%',
    height: '100%',
    zIndex: '999999',
    pointerEvents: 'none',
  });
  document.body.appendChild(canvasEl);
  return canvasEl;
}

function ensureShader(aesthetics = getAesthetics()) {
  if (shaderCtrl) return shaderCtrl;

  const canvas = ensureCanvas();
  shaderCtrl = createShader({
    canvas,
    ...shaderOptionsFromAesthetics(aesthetics),
  });

  return shaderCtrl;
}

function isEnabled() {
  if (config.navMode === 'off' || config.linkScope === 'off') return false;
  if (config.respectReducedMotion && prefersReducedMotion()) return false;
  return true;
}

async function fetchPage(href) {
  const res = await fetch(href, {
    credentials: 'same-origin',
    headers: { 'X-Glimm-PJAX': '1' },
  });
  if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
  return res.text();
}

function parseAndSwapFromHtml(html, href) {
  phase('pjax:swap:start', { href, htmlBytes: html?.length ?? 0 });
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const styleSnapshot = snapshotPageStyles();
  syncPageStyles(doc);

  const nextRegion = findContentRegion(doc);
  const currentRegion = findContentRegion(document);

  if (!areContentRegionsCompatible(currentRegion, nextRegion)) {
    throw new Error('PJAX: incompatible content regions');
  }

  try {
    swapContentRegions(currentRegion, nextRegion);
  } catch (err) {
    restorePageStyles(styleSnapshot);
    throw err;
  }

  document.title = doc.title;

  runPostNavigationUpdates(doc, href, doc.title);

  // Nudge container-query layouts (block theme grids) to recalculate.
  requestAnimationFrame(() => {
    void document.body.offsetHeight;
  });

  if (config.scrollToTop) {
    window.scrollTo(0, 0);
  }

  document.dispatchEvent(
    new CustomEvent('glimm:navigate', { detail: { url: href, mode: 'pjax' } })
  );

  phase('pjax:swap:done', { href, title: doc.title });
  return findContentRegion(document);
}

function startContentFadeIn(region, aesthetics = getAesthetics()) {
  if (!region) {
    warn('fade-in:skipped', 'no content region');
    return;
  }

  const { fadeOpacity, fadeInMs } = getContentFadeTiming(aesthetics);
  phase('fade-in:start', { fadeOpacity, fadeInMs });
  setRegionOpacity(region, fadeOpacity, null);
  animateRegionOpacity(region, fadeOpacity, 1, fadeInMs).then(() => {
    clearRegionOpacity(region);
    phase('fade-in:done');
  });
}

function midpointNavigate(href) {
  window.location.assign(href);
}

function performMidpointAction({
  mode,
  prefetchedHtml,
  targetHref,
  aesthetics,
  fadeOpacity,
  fadeInMs,
}) {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      try {
        if (mode === 'pjax') {
          if (prefetchedHtml == null) {
            throw new Error('PJAX: prefetch missing at midpoint');
          }
          const newRegion = parseAndSwapFromHtml(prefetchedHtml, targetHref);
          history.pushState({ glimm: true, url: targetHref }, '', targetHref);
          startContentFadeIn(newRegion, aesthetics);
        } else if (mode === 'midpoint') {
          savePendingHandoff({
            href: targetHref,
            aesthetics,
            contentFadeOpacity: fadeOpacity,
            fadeInMs,
            startedAt: Date.now(),
          });
          midpointNavigate(targetHref);
        }
      } catch (err) {
        warn('sweep:midpoint-error', err);
        clearRegionOpacity(findContentRegion(document));
        window.location.assign(targetHref);
      } finally {
        resolve();
      }
    });
  });
}

async function runTransition(href, mode) {
  if (navigating) {
    warn('transition:blocked', 'already navigating', href);
    return;
  }

  const targetHref = normalizeHref(href);
  const t0 = performance.now();
  phase('transition:start', { href: targetHref, mode });

  closeMobileNavigation();

  const ctrl = ensureShader();
  if (!ctrl) {
    warn('transition:fallback', 'no shader controller');
    window.location.assign(targetHref);
    return;
  }

  navigating = true;

  if (activeHandle?.cancel) {
    phase('transition:cancel-previous');
    activeHandle.cancel();
    activeHandle = null;
  }

  const aesthetics = getAesthetics();
  const { fadeOpacity, fadeOutMs, fadeInMs } = getContentFadeTiming(aesthetics);
  const currentRegion = findContentRegion(document);
  let prefetchedHtml = null;

  phase('transition:config', {
    sweepMs: aesthetics.sweepMs,
    midpoint: aesthetics.midpoint,
    fadeOutMs,
    fadeInMs,
    fadeOpacity,
    hasRegion: Boolean(currentRegion),
  });

  try {
    // Phase 1: fade out + prefetch in parallel (both must finish before sweep).
    const fadePromise = currentRegion
      ? animateRegionOpacity(currentRegion, 1, fadeOpacity, fadeOutMs).then(() => {
          phase('fade-out:done', { ms: Math.round(performance.now() - t0) });
        })
      : Promise.resolve().then(() => {
          phase('fade-out:skipped', 'no content region');
        });

    const fetchPromise = mode === 'pjax'
      ? fetchPage(targetHref).then((html) => {
          phase('prefetch:done', {
            ms: Math.round(performance.now() - t0),
            htmlBytes: html?.length ?? 0,
          });
          return html;
        })
      : Promise.resolve(null);

    phase('fade-out:start', { fadeOutMs });
    if (mode === 'pjax') phase('prefetch:start', { href: targetHref });

    const [, html] = await Promise.all([fadePromise, fetchPromise]);
    prefetchedHtml = html;

    resetShaderForSweep(ctrl);

    // Phase 2: sweep — swap at midpoint (sync), fade in during second half.
    phase('sweep:start', {
      ms: Math.round(performance.now() - t0),
      sweepMs: aesthetics.sweepMs,
      midpoint: aesthetics.midpoint,
    });

    activeHandle = playSweep(ctrl, getSweepOptions({
      onMidpoint: () => {
        phase('sweep:midpoint', {
          ms: Math.round(performance.now() - t0),
          progress: ctrl.getProgress(),
          alpha: ctrl.getAlpha(),
        });
        return performMidpointAction({
          mode,
          prefetchedHtml,
          targetHref,
          aesthetics,
          fadeOpacity,
          fadeInMs,
        });
      },
      onComplete: () => {
        navigating = false;
        phase('sweep:complete', {
          ms: Math.round(performance.now() - t0),
          progress: ctrl.getProgress(),
          alpha: ctrl.getAlpha(),
        });
      },
    }));

    await activeHandle.done;
    phase('transition:done', { ms: Math.round(performance.now() - t0) });
  } catch (err) {
    warn('transition:error', err);
    clearRegionOpacity(findContentRegion(document));
    window.location.assign(targetHref);
  } finally {
    navigating = false;
  }
}

function handleClick(event) {
  if (!isEnabled()) {
    return;
  }
  if (navigating) {
    phase('click:blocked', { reason: 'navigating' });
    return;
  }
  if (event.defaultPrevented) return;
  if (event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const anchor = event.target.closest('a[href]');
  if (!anchor) return;
  if (shouldSkipLink(anchor, config)) {
    phase('click:skipped', { href: anchor.href, reason: 'shouldSkipLink' });
    return;
  }
  if (!matchesLinkScope(anchor, config)) {
    phase('click:skipped', { href: anchor.href, reason: 'linkScope' });
    return;
  }

  event.preventDefault();
  const targetHref = normalizeHref(anchor.href);
  log('intercept', targetHref, config.navMode);
  runTransition(targetHref, config.navMode);
}

async function handlePopState() {
  if (config.navMode !== 'pjax') return;

  const href = window.location.href;
  if (isSamePage(href)) return;

  if (navigating) {
    cancelActiveTransition('popstate');
  }

  const mode = config.popstateMode || 'pjax_no_sweep';
  log('popstate', mode, href);

  if (mode === 'reload') {
    window.location.reload();
    return;
  }

  try {
    if (mode === 'pjax_sweep') {
      await runTransition(href, 'pjax');
      return;
    }

    const html = await fetchPage(href);
    parseAndSwapFromHtml(html, href);
  } catch (err) {
    log('popstate error', err);
    window.location.assign(href);
  }
}

function resumeMidpointHandoff() {
  const pending = readPendingHandoff();
  if (!pending) return;

  clearPendingHandoff();

  const aesthetics = pending.aesthetics || getAesthetics();
  const ctrl = ensureShader(aesthetics);
  if (!ctrl) return;

  const region = findContentRegion(document);
  const fadeOpacity = pending.contentFadeOpacity ?? getContentFadeTiming(aesthetics).fadeOpacity;
  const fadeInMs = pending.fadeInMs ?? getContentFadeTiming(aesthetics).fadeInMs;

  log('resume midpoint handoff', pending.href);

  removeMidpointLandingStyle();

  ctrl.setProgress(aesthetics.midpoint ?? 0.5);
  ctrl.setAlpha(1);

  if (region) {
    setRegionOpacity(region, fadeOpacity, null);
    animateRegionOpacity(region, fadeOpacity, 1, fadeInMs).then(() => {
      clearRegionOpacity(region);
    });
  }

  playSweep(ctrl, aestheticsToSweepOptions({
    ...aesthetics,
    sweepMs: Math.max(200, (aesthetics.outroMs || 350) * 2),
    midpoint: 0.01,
    onMidpoint: () => {},
    onComplete: () => {
      ctrl.setAlpha(0);
      log('midpoint handoff complete');
    },
  }));
}

function init() {
  if (config.navMode === 'off') {
    phase('init:off', { navMode: config.navMode });
    return;
  }

  resumeMidpointHandoff();

  if (!isEnabled()) {
    phase('init:disabled', {
      navMode: config.navMode,
      linkScope: config.linkScope,
      reducedMotion: prefersReducedMotion(),
      respectReducedMotion: config.respectReducedMotion,
    });
    return;
  }

  document.addEventListener('click', handleClick, true);
  window.addEventListener('popstate', handlePopState);
  phase('init:ready', {
    navMode: config.navMode,
    debug: config.debug,
    sweepMs: config.aesthetics?.sweepMs,
    midpoint: config.aesthetics?.midpoint,
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
